import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAxiosError } from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { Filter, Loader2, Pencil, Plus, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useAuth } from '../../hook_au/useAuth';
import { useAppSettings } from '../../hook/useAppSettings';
import apiClient from '../../services/apiClient';

type ProjectStatus = 'planificacion' | 'en_progreso' | 'presupuestado' | 'finalizado';

type Project = {
  id: string;
  ref: string;
  name: string;
  type: string;
  client: string;
  address: string;
  status: ProjectStatus;
  total_budget: number;
  created_at: string;
  estimated_margin?: number | null;
  real_margin?: number | null;
  subprojects?: number | null;
};

type ProjectsListResponse = {
  items: Project[];
  pagination: {
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  };
};

type ProjectForm = {
  ref: string;
  name: string;
  type: string;
  client: string;
  address: string;
  status: ProjectStatus;
  total_budget: string;
};

type AdvancedFilters = {
  type: string;
  client: string;
  minBudget: string;
  maxBudget: string;
  createdFrom: string;
  createdTo: string;
};

const STATUS_LABELS: Record<ProjectStatus, string> = {
  planificacion: 'Planificación',
  en_progreso: 'En progreso',
  presupuestado: 'Presupuestado',
  finalizado: 'Finalizado',
};

const STATUS_BADGE_VARIANTS: Record<ProjectStatus, 'secondary' | 'inProgress' | 'quoted' | 'finished' > = {
  planificacion: 'secondary',
  en_progreso: 'inProgress',
  presupuestado: 'quoted',
  finalizado: 'finished',
};

const EMPTY_FORM: ProjectForm = {
  ref: '',
  name: '',
  type: '',
  client: '',
  address: '',
  status: 'planificacion',
  total_budget: '',
};

const INITIAL_ADVANCED_FILTERS: AdvancedFilters = {
  type: '',
  client: '',
  minBudget: '',
  maxBudget: '',
  createdFrom: '',
  createdTo: '',
};

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface ProjectsProps {
  onSelectProject: (projectId: string) => void;
}

export function Projects({ onSelectProject }: ProjectsProps) {
  const { token } = useAuth();
  const { formatCurrency, formatPercent } = useAppSettings();
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalProjects, setTotalProjects] = useState(0);
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [pageInput, setPageInput] = useState('1');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ProjectStatus>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<ProjectForm>(EMPTY_FORM);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>(INITIAL_ADVANCED_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<AdvancedFilters>(INITIAL_ADVANCED_FILTERS);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const fetchProjects = useCallback(async () => {
    if (!token) {
      setProjects([]);
      return;
    }

    setLoading(true);
    setError(null);

    const trimmedType = appliedFilters.type.trim();
    const trimmedClient = appliedFilters.client.trim();
    const minBudgetRaw = appliedFilters.minBudget.trim();
    const maxBudgetRaw = appliedFilters.maxBudget.trim();

    try {
      const response = await apiClient.get<ProjectsListResponse>('/projects/list', {
        params: {
          limit,
          offset,
          search: debouncedSearchTerm.trim() || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          type: trimmedType || undefined,
          client: trimmedClient || undefined,
          min_budget: minBudgetRaw ? Number(minBudgetRaw) : undefined,
          max_budget: maxBudgetRaw ? Number(maxBudgetRaw) : undefined,
          created_from: appliedFilters.createdFrom || undefined,
          created_to: appliedFilters.createdTo || undefined,
        },
      });

      const loadedProjects = response.data.items.map((project) => ({
        ...project,
        total_budget: Number(project.total_budget),
        subprojects: project.subprojects ?? 0,
        real_margin:
          project.real_margin === null || project.real_margin === undefined
            ? null
            : Number(project.real_margin),
      }));

      setProjects(loadedProjects);
      setTotalProjects(response.data.pagination.total ?? loadedProjects.length);
    } catch (requestError) {
      if (isAxiosError(requestError) && requestError.response?.status === 401) {
        setProjects([]);
        setTotalProjects(0);
        toast.error('La sesión expiró. Vuelve a iniciar sesión para continuar.');
      } else {
        setError('No pudimos cargar los proyectos. Inténtalo de nuevo en unos segundos.');
      }
    } finally {
      setLoading(false);
    }
  }, [token, limit, offset, debouncedSearchTerm, statusFilter, appliedFilters]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const openCreateModal = () => {
    setFormValues(EMPTY_FORM);
    setEditingProject(null);
    setIsModalOpen(true);
  };

  const openEditModal = (project: Project) => {
    setFormValues({
      ref: project.ref,
      name: project.name,
      type: project.type,
      client: project.client,
      address: project.address,
      status: project.status,
      total_budget: String(project.total_budget ?? ''),
    });
    setEditingProject(project);
    setIsModalOpen(true);
  };

  const handleInputChange = (field: keyof ProjectForm, value: string) => {
    setFormValues((previous) => ({ ...previous, [field]: value }));
  };

  const handleAdvancedFilterChange = (field: keyof AdvancedFilters, value: string) => {
    setAdvancedFilters((previous) => ({ ...previous, [field]: value }));
  };

  const handleApplyAdvancedFilters = () => {
    const minBudgetRaw = advancedFilters.minBudget.trim();
    const maxBudgetRaw = advancedFilters.maxBudget.trim();
    const parsedMinBudget = minBudgetRaw ? Number(minBudgetRaw) : undefined;
    const parsedMaxBudget = maxBudgetRaw ? Number(maxBudgetRaw) : undefined;

    if (minBudgetRaw && Number.isNaN(parsedMinBudget)) {
      toast.error('Introduce un presupuesto mínimo válido.');
      return;
    }
    if (maxBudgetRaw && Number.isNaN(parsedMaxBudget)) {
      toast.error('Introduce un presupuesto máximo válido.');
      return;
    }
    if (
      parsedMinBudget !== undefined &&
      parsedMaxBudget !== undefined &&
      parsedMinBudget > parsedMaxBudget
    ) {
      toast.error('El presupuesto mínimo no puede ser mayor que el máximo.');
      return;
    }

    if (advancedFilters.createdFrom && advancedFilters.createdTo) {
      const fromDate = new Date(advancedFilters.createdFrom);
      const toDate = new Date(advancedFilters.createdTo);
      if (fromDate > toDate) {
        toast.error('La fecha inicial no puede ser posterior a la final.');
        return;
      }
    }

    setAppliedFilters({ ...advancedFilters });
    setOffset(0);
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters(INITIAL_ADVANCED_FILTERS);
    setAppliedFilters(INITIAL_ADVANCED_FILTERS);
    setOffset(0);
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters((previous) => !previous);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!token) {
      toast.error('La sesión expiró. Inicia sesión de nuevo.');
      return;
    }

    if (!formValues.total_budget || Number.isNaN(Number(formValues.total_budget))) {
      toast.error('Introduce un presupuesto total válido.');
      return;
    }

    const payload = {
      ref: formValues.ref.trim().toUpperCase(),
      name: formValues.name.trim(),
      type: formValues.type.trim(),
      client: formValues.client.trim(),
      address: formValues.address.trim(),
      status: formValues.status,
      total_budget: Number(formValues.total_budget),
    };

    setSubmitting(true);

    try {
      if (editingProject) {
        await apiClient.put(`/projects/${editingProject.id}`, payload);
        toast.success('Proyecto actualizado correctamente.');
      } else {
        await apiClient.post('/projects/create', payload);
        toast.success('Proyecto creado correctamente.');
      }

      setIsModalOpen(false);
      await fetchProjects();
    } catch (requestError) {
      if (isAxiosError(requestError)) {
        if (requestError.response?.status === 409) {
          toast.error('Ya existe un proyecto con esa referencia.');
        } else if (requestError.response?.status === 401) {
          toast.error('La sesión expiró. Inicia sesión de nuevo.');
        } else if (requestError.response?.status === 422) {
          toast.error('Hay datos inválidos. Revisa los campos e inténtalo de nuevo.');
        } else {
          toast.error('No se pudo guardar el proyecto. Inténtalo más tarde.');
        }
      } else {
        toast.error('No se pudo guardar el proyecto. Inténtalo más tarde.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formattedProjects = useMemo(
    () =>
      projects.map((project) => {
        const normalizedMarginPct =
          project.real_margin === null || project.real_margin === undefined
            ? null
            : project.real_margin > 1
              ? project.real_margin
              : project.real_margin * 100;

        return {
          ...project,
          readableStatus: STATUS_LABELS[project.status],
          readableCreatedAt: format(new Date(project.created_at), "dd 'de' MMMM yyyy", { locale: es }),
          readableBudget: formatCurrency(project.total_budget),
          readableRealMargin:
            normalizedMarginPct === null
              ? '-'
              : formatPercent(normalizedMarginPct, { maximumFractionDigits: 0 }),
        };
      }),
    [projects, formatCurrency, formatPercent],
  );
  const titleCount = totalProjects;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil((totalProjects || 0) / limit));

  useEffect(() => {
    setPageInput(String(currentPage));
  }, [currentPage]);

  const commitPageInput = () => {
    const parsed = Number(pageInput);
    if (!Number.isInteger(parsed)) {
      setPageInput(String(currentPage));
      return;
    }
    const nextPage = Math.min(totalPages, Math.max(1, parsed));
    setOffset((nextPage - 1) * limit);
  };

return (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl text-gray-900 mb-2">Gestión de Proyectos</h1>
        <p className="text-gray-600">Administra todos los proyectos de interiorismo</p>
      </div>
      <Button className="bg-blue-600 hover:bg-blue-700" onClick={openCreateModal}>
        <Plus className="w-4 h-4 mr-2" />
        Nuevo Proyecto
      </Button>
    </div>

    <Dialog
      open={isModalOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && submitting) return;
        setIsModalOpen(nextOpen);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editingProject ? 'Editar proyecto' : 'Nuevo proyecto'}</DialogTitle>
          <DialogDescription>
            Ref, nombre, tipo, cliente, dirección, estado y presupuesto son obligatorios.
          </DialogDescription>
        </DialogHeader>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Referencia</label>
            <Input
              value={formValues.ref}
              onChange={(event) => handleInputChange('ref', event.target.value.toUpperCase())}
              placeholder="PROY-001"
              required
              disabled={submitting}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Nombre</label>
            <Input
              value={formValues.name}
              onChange={(event) => handleInputChange('name', event.target.value)}
              placeholder="Nombre del proyecto"
              required
              disabled={submitting}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Tipo</label>
            <Input
              value={formValues.type}
              onChange={(event) => handleInputChange('type', event.target.value)}
              placeholder="Edificio residencial"
              required
              disabled={submitting}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Cliente</label>
            <Input
              value={formValues.client}
              onChange={(event) => handleInputChange('client', event.target.value)}
              placeholder="Empresa o persona responsable"
              required
              disabled={submitting}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Dirección</label>
            <Input
              value={formValues.address}
              onChange={(event) => handleInputChange('address', event.target.value)}
              placeholder="Ubicación del proyecto"
              required
              disabled={submitting}
            />
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Estado</label>
            <Select
              value={formValues.status}
              onValueChange={(value) => handleInputChange('status', value as ProjectStatus)}
              disabled={submitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona un estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="planificacion">Planificación</SelectItem>
                <SelectItem value="en_progreso">En progreso</SelectItem>
                <SelectItem value="presupuestado">Presupuestado</SelectItem>
                <SelectItem value="finalizado">Finalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <label className="text-sm font-medium text-gray-700">Presupuesto total (€)</label>
            <Input
              value={formValues.total_budget}
              onChange={(event) => handleInputChange('total_budget', event.target.value)}
              type="number"
              step="0.01"
              min="0"
              placeholder="150000"
              required
              disabled={submitting}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <Card className="shadow-sm">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre de proyecto"
                value={searchTerm}
                onChange={(event) => {
                  setSearchTerm(event.target.value);
                  setOffset(0);
                }}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value as 'all' | ProjectStatus);
                setOffset(0);
              }}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="en_progreso">En progreso</SelectItem>
                <SelectItem value="planificacion">Planificación</SelectItem>
                <SelectItem value="finalizado">Finalizado</SelectItem>
                <SelectItem value="presupuestado">Presupuestado</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={toggleAdvancedFilters}>
              <Filter className="w-4 h-4 mr-2" />
              {showAdvancedFilters ? 'Ocultar filtros' : 'Más filtros'}
            </Button>
          </div>
        </div>
        {showAdvancedFilters && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <div className="grid gap-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Tipo</label>
                  <Input
                    value={advancedFilters.type}
                    onChange={(event) => handleAdvancedFilterChange('type', event.target.value)}
                    placeholder="Residencial, Comercial..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Cliente</label>
                  <Input
                    value={advancedFilters.client}
                    onChange={(event) => handleAdvancedFilterChange('client', event.target.value)}
                    placeholder="Nombre del cliente"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Presupuesto mínimo (€)</label>
                  <Input
                    type="number"
                    min="0"
                    value={advancedFilters.minBudget}
                    onChange={(event) => handleAdvancedFilterChange('minBudget', event.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Presupuesto máximo (€)</label>
                  <Input
                    type="number"
                    min="0"
                    value={advancedFilters.maxBudget}
                    onChange={(event) => handleAdvancedFilterChange('maxBudget', event.target.value)}
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Creado desde</label>
                  <Input
                    type="date"
                    value={advancedFilters.createdFrom}
                    onChange={(event) => handleAdvancedFilterChange('createdFrom', event.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Creado hasta</label>
                  <Input
                    type="date"
                    value={advancedFilters.createdTo}
                    onChange={(event) => handleAdvancedFilterChange('createdTo', event.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters} disabled={loading}>
                  Limpiar filtros
                </Button>
                <Button
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={handleApplyAdvancedFilters}
                  disabled={loading}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

      <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Proyectos ({titleCount})</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre del Proyecto</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-center">Subproyectos</TableHead>
                  <TableHead className="text-center">Presupuesto</TableHead>
                  <TableHead className="text-center">Margen Real</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formattedProjects.map((project) => (
                  <TableRow
                    key={project.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => onSelectProject(project.id)}
                  >
                    <TableCell>
                      <div>
                        <p className="text-gray-900">{project.name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{project.client}</TableCell>
                    <TableCell className="text-gray-600">{project.type}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant={STATUS_BADGE_VARIANTS[project.status]}>
                        {project.readableStatus}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof project.subprojects === 'number' ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700">
                          {project.subprojects} subproyectos
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-900 text-center">{project.readableBudget}</TableCell>
                    <TableCell
                      className={
                        `text-center ${project.readableRealMargin !== '-' ? 'text-green-600' : 'text-gray-400'}`
                      }
                    >
                      {project.readableRealMargin}
                    </TableCell>
                    <TableCell className="text-center" onClick={(event) => event.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(project)}>
                        <Pencil className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {formattedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-gray-500 py-8">
                      No hay proyectos con los filtros actuales.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((current) => Math.max(0, current - limit))}
            disabled={offset === 0 || loading}
          >
            Anterior
          </Button>
          <div className="flex items-center gap-2">
            <Input
              value={pageInput}
              onChange={(event) => setPageInput(event.target.value)}
              onBlur={commitPageInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitPageInput();
              }}
              className="h-9 w-16 text-center"
              disabled={loading}
            />
            <span className="text-sm text-gray-600">/ {totalPages}</span>
          </div>
                    <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((current) => current + limit)}
            disabled={loading || currentPage >= totalPages}
          >
            Siguiente
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((totalPages - 1) * limit)}
            disabled={loading || currentPage >= totalPages}
          >
            Última página
          </Button>
          <Select
            value={String(limit)}
            onValueChange={(value) => {
              setLimit(Number(value));
              setOffset(0);
            }}
            disabled={loading}
          >
            <SelectTrigger className="w-24">
              <SelectValue placeholder="Límite" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  </div>
);
}
